import { AvailableTimesUnit } from "types/available-times";
import { Experience } from "types/experience";
import { ExperienceActivity } from "types/experience-activity";
import { WebRuleBookingType } from "types/web-rule";
import {
    AvailabilityState,
    getAvailabilityState,
    isSectionShowable,
} from "utils/availability-utils";

/**
 * Filters and sorts available times data
 * @param availableTimesData The data from the API
 * @returns Sorted and filtered sections
 */
function filterAndSortAvailableTimes(
    availableTimesData: AvailableTimesUnit,
    webRuleBookingType: WebRuleBookingType = WebRuleBookingType.Booking,
    guests: number = 0,
    isBookingRequest: boolean = false
) {
    let availableTimes = availableTimesData.sections;

    // Do not display sections with no active web rules initially
    availableTimes = availableTimes.filter((section) => section.timeSets.length > 0);

    availableTimes.forEach((section) => {
        // Remove expired times
        section.timeSets.forEach((timeSet) => {
            timeSet.times = timeSet.times.filter((time) => !time.timeExpired);
        });

        // Filter by booking type first, before calculating availability
        section.timeSets = section.timeSets.filter(
            (timeSet) => timeSet.bookingType === webRuleBookingType
        );

        // Compute availability state. Booking requests are always treated as available
        // so guests can still select times regardless of seat counts.
        section.availabilityState = isBookingRequest
            ? AvailabilityState.Available
            : getAvailabilityState(section.timeSets, guests);
    });

    // Sort sections on sort order
    availableTimes.sort((a, b) => a.sortOrder - b.sortOrder);

    // Sort sections to show unavailable sections last
    availableTimes.sort((a, b) => {
        const aShowable = isSectionShowable(a.availabilityState);
        const bShowable = isSectionShowable(b.availabilityState);
        return aShowable === bShowable ? 0 : aShowable ? -1 : 1;
    });

    return availableTimes;
}

/**
 * Determines if an activity is unavailable based on its timeSets
 * @param activity The experience activity to check
 * @param webRuleBookingType The booking type to filter by
 * @param guests The number of guests
 * @param isBookingRequest Whether this is a booking request
 * @returns True if the activity is unavailable
 */
function isActivityUnavailable(
    activity: ExperienceActivity,
    webRuleBookingType: WebRuleBookingType,
    guests: number,
    isBookingRequest: boolean
): boolean {
    // Filter timeSets by booking type
    const filteredTimeSets = activity.timeSets.filter(
        (timeSet) => timeSet.bookingType === webRuleBookingType
    );

    // Activity is unavailable if no timeSets match the booking type
    if (filteredTimeSets.length === 0) {
        return true;
    }

    // Check if all times are expired or unavailable for the group size
    const hasAvailableTime = filteredTimeSets.some((timeSet) => {
        return timeSet.times.some((time) => {
            if (time.timeExpired) return false;
            if (
                !isBookingRequest &&
                time.unavailableForGroupSize &&
                time.waitListUnavailableForGroupSize
            )
                return false;
            return true;
        });
    });

    return !hasAvailableTime;
}

/**
 * Filters and sorts experiences data
 * An experience is only available if ALL its activities are available
 * @param experiences The experiences from the API
 * @param webRuleBookingType The booking type to filter by
 * @param guests The number of guests
 * @param isBookingRequest Whether this is a booking request
 * @param sectionIdsFilter Optional array of section IDs to filter by
 * @returns Sorted and filtered experiences
 */
function filterAndSortExperiences(
    experiences: Experience[],
    webRuleBookingType: WebRuleBookingType = WebRuleBookingType.Booking,
    guests: number = 0,
    isBookingRequest: boolean = false,
    sectionIdsFilter?: number[]
): Experience[] {
    const filteredExperiences = experiences.map((exp) => exp.clone());

    filteredExperiences.forEach((experience) => {
        // Activities[0] is the chain starter in find-experience-chains; sort by sortOrder
        // so the chain sequence follows the admin's configured order.
        experience.activities.sort((a, b) => a.sortOrder - b.sortOrder);

        // Filter by sectionIds if provided
        // An experience is included only if ALL its activities match the filter
        if (sectionIdsFilter && sectionIdsFilter.length > 0) {
            const allActivitiesMatchFilter = experience.activities.every((activity) =>
                sectionIdsFilter.includes(activity.sectionId)
            );

            if (!allActivitiesMatchFilter) {
                experience.unavailable = true;
                return;
            }
        }

        // A request-only experience carries Inquiry rules instead of Experience ones, and has no
        // bookable seats to run out of — the same footing as the booking-request flow (BOOK-3911).
        const experienceBookingType = experience.bookingRequestOnly
            ? WebRuleBookingType.Request
            : webRuleBookingType;
        const asRequest = isBookingRequest || experience.bookingRequestOnly;

        // Process each activity
        experience.activities.forEach((activity) => {
            // Remove expired times
            activity.timeSets.forEach((timeSet) => {
                timeSet.times = timeSet.times.filter((time) => !time.timeExpired);
            });

            // Filter by booking type
            activity.timeSets = activity.timeSets.filter(
                (timeSet) => timeSet.bookingType === experienceBookingType
            );
        });

        // An experience is unavailable if ANY activity is unavailable. An information-only
        // experience has no times by design, so it is offered wherever it has an open text row.
        const hasUnavailableActivity = experience.activities.some((activity) =>
            experience.informationOnly
                ? activity.timeSets.every((timeSet) => timeSet.isClosed)
                : isActivityUnavailable(activity, experienceBookingType, guests, asRequest)
        );

        experience.unavailable = hasUnavailableActivity;
    });

    // Sort by sort order
    filteredExperiences.sort((a, b) => a.sortOrder - b.sortOrder);

    // Sort to show unavailable experiences last
    filteredExperiences.sort((a, b) =>
        a.unavailable === b.unavailable ? 0 : a.unavailable ? 1 : -1
    );

    return filteredExperiences;
}

export { filterAndSortAvailableTimes, filterAndSortExperiences };
